/**
 * Layout Configuration - Single Source of Truth
 *
 * All layout constants for public pages live here.
 * Change a value here = updates everywhere automatically.
 */

import breakpoints from "@shared/theme/props/breakpoints";

// ============================================================================
// Layout Preset Configuration
// ============================================================================

/**
 * Available layout presets for landing pages (events/memberships)
 *
 * - classic: Two-column layout with header image, tabs, sidebar CTA
 * - hero: Single-column hero banner layout, no sidebar
 */
export type LayoutPreset = "classic" | "hero";

/**
 * Default layout preset when not specified
 */
export const DEFAULT_LAYOUT_PRESET: LayoutPreset = "classic";

/**
 * Layout preset defaults
 *
 * Each preset can define its own default maxWidth.
 * These are used by createPublicPage when no explicit maxWidth is provided.
 *
 * Resolution order:
 * 1. createPublicPage({ maxWidth: "..." }) - explicit override
 * 2. LAYOUT_PRESET_DEFAULTS[preset].maxWidth - preset default
 * 3. MAX_WIDTHS.content - global fallback
 */
export const LAYOUT_PRESET_DEFAULTS = {
  classic: { maxWidth: "content" }, // 1520px
  hero: { maxWidth: "content" }, // 1520px (single-column, no sidebar)
} as const;

/**
 * Layout configuration interface
 *
 * This will eventually be populated from useBrandingConfig hook
 * to allow venue-level layout customization.
 */
export interface LayoutConfig {
  /** Which layout preset to use */
  layoutPreset: LayoutPreset;
}

/**
 * Default layout config
 */
export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  layoutPreset: DEFAULT_LAYOUT_PRESET,
};

/**
 * Placeholder for future branding config integration
 *
 * TODO: Replace with actual useBrandingConfig hook that fetches
 * venue-specific layout settings from the database.
 *
 * @example Future usage:
 * ```ts
 * // In adapter or page component:
 * const brandingConfig = useBrandingConfig(venueSlug);
 * const layoutPreset = brandingConfig?.layoutPreset ?? DEFAULT_LAYOUT_PRESET;
 * ```
 */
export function getLayoutPresetFromBranding(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _venueSlug?: string
): LayoutPreset {
  // TODO: Implement actual branding config lookup
  // For now, always return the default preset
  return DEFAULT_LAYOUT_PRESET;
}

// ============================================================================
// Max Width Configuration
// ============================================================================

/**
 * Max width presets for content containers
 */
export const MAX_WIDTHS = {
  /** Standard content width (1520px) - detail pages, headers */
  content: "1520px",
  /** Narrow width (1200px) - forms, focused content */
  narrow: `${breakpoints.lg}px`,
  /** Full width - checkout, edge-to-edge */
  full: "100%",
} as const;

export type MaxWidthKey = keyof typeof MAX_WIDTHS;

/**
 * Responsive padding for content containers
 * Applied horizontally (left/right) - breathing room from browser edges
 * Note: sm breakpoint = 768px in our theme (not Chakra's default 480px)
 */
export const CONTENT_PADDING = {
  base: "20px",
  sm: "40px",
} as const;

/**
 * Navbar configuration presets
 */
export const NAVBAR_PRESETS = {
  /** Detail pages (event, membership) - classic layout */
  detail: {
    showCart: true,
    showTimer: false,
    showLogo: true,
    showUserMenu: true,
    showMobileModal: true,
    showDashboardLink: true,
    showManageLink: true,
    showLoginSignup: true,
    hideUserName: false,
    showBanners: true,
    isDetailPage: true,
    noBorder: false,
    maxWidth: "content" as MaxWidthKey,
  },
  /** Detail pages - hero layout (no border for seamless hero integration) */
  "detail-hero": {
    showCart: true,
    showTimer: false,
    showLogo: true,
    showUserMenu: true,
    showMobileModal: true,
    showDashboardLink: true,
    showManageLink: true,
    showLoginSignup: true,
    hideUserName: false,
    showBanners: true,
    isDetailPage: true,
    noBorder: true, // Seamless connection to hero
    maxWidth: "content" as MaxWidthKey,
    transparent: true,
  },
  /** Checkout flow */
  checkout: {
    showCart: false,
    showTimer: true,
    showLogo: true,
    showUserMenu: true,
    showMobileModal: true,
    showDashboardLink: false,
    showManageLink: false,
    showLoginSignup: false,
    hideUserName: false,
    showBanners: true,
    isDetailPage: false,
    noBorder: true,
    maxWidth: "full" as MaxWidthKey,
  },
  /** Branded checkout review pages (hold links, tracking links) */
  checkoutReview: {
    showCart: false,
    showTimer: true,
    showLogo: true,
    showUserMenu: true,
    showMobileModal: true,
    showDashboardLink: false,
    showManageLink: false,
    showLoginSignup: false,
    hideUserName: false,
    showBanners: false,
    isDetailPage: false,
    noBorder: false,
    maxWidth: "full" as MaxWidthKey,
  },
  /** Branded subpages (seating maps, ticket packages) */
  subpage: {
    showCart: true,
    showTimer: false,
    showLogo: true,
    showUserMenu: true,
    showMobileModal: true,
    showDashboardLink: true,
    showManageLink: true,
    showLoginSignup: true,
    hideUserName: false,
    showBanners: true,
    isDetailPage: true,
    noBorder: false,
    maxWidth: "full" as MaxWidthKey,
  },
  /** Discovery/browse pages */
  discovery: {
    showCart: false,
    showTimer: false,
    showLogo: true,
    showUserMenu: true,
    showMobileModal: true,
    showDashboardLink: true,
    showManageLink: true,
    showLoginSignup: true,
    hideUserName: false,
    showBanners: false,
    isDetailPage: false,
    noBorder: false,
    maxWidth: "content" as MaxWidthKey,
  },
} as const;

export type NavbarPresetKey = keyof typeof NAVBAR_PRESETS;
export type NavbarPreset = (typeof NAVBAR_PRESETS)[NavbarPresetKey];

/**
 * Maps layout presets to their default navbar presets
 */
export const LAYOUT_TO_NAVBAR_MAP: Record<LayoutPreset, NavbarPresetKey> = {
  classic: "detail",
  hero: "detail-hero",
};

/**
 * Theme/Color mode configuration for layouts
 *
 * Controls dark/light mode and custom colors for the layout.
 * Eventually will be populated from useBrandingConfig hook.
 */
export type ColorMode = "light" | "dark";

export interface LayoutThemeConfig {
  /** Color mode for the layout */
  colorMode: ColorMode;
  /** Whether to force the color mode (user can't toggle) */
  forceColorMode?: boolean;
  /**
   * Custom primary color (hex code, e.g. "#FF5500")
   * Will generate a full palette using colorToPalette
   */
  primaryColor?: string;
  /**
   * Custom secondary color (hex code, e.g. "#0055FF")
   * Will generate a full palette using colorToPalette
   */
  secondaryColor?: string;
  /**
   * Custom logo image URL (from branding config)
   */
  logoImage?: string;
  /**
   * Custom logo link URL (from branding config)
   */
  logoLinkUrl?: string;
  /**
   * Custom heading font family (e.g. "Poppins", "Montserrat")
   */
  headingFont?: string;
  /**
   * Custom body font family (e.g. "Open Sans", "Roboto")
   */
  bodyFont?: string;
}

/**
 * Default theme config - light mode, not forced
 */
export const DEFAULT_THEME_CONFIG: LayoutThemeConfig = {
  colorMode: "light",
  forceColorMode: false,
};

export const DEFAULT_SHOWPASS_THEME_CONFIG: LayoutThemeConfig = {
  ...DEFAULT_THEME_CONFIG,
  forceColorMode: true, // force light mode for showpass pages
};

/**
 * Mock theme configs for testing
 * TODO: Replace with useBrandingConfig hook data
 */
export const MOCK_THEME_CONFIGS = {
  light: { colorMode: "light", forceColorMode: false } as LayoutThemeConfig,
  dark: { colorMode: "dark", forceColorMode: false } as LayoutThemeConfig,
  forcedDark: { colorMode: "dark", forceColorMode: true } as LayoutThemeConfig,
  forcedLight: {
    colorMode: "light",
    forceColorMode: true,
  } as LayoutThemeConfig,
} as const;

/**
 * Combined layout configuration
 * Import this for access to all layout constants
 */
export const LAYOUT_CONFIG = {
  maxWidths: MAX_WIDTHS,
  padding: CONTENT_PADDING,
  navbar: NAVBAR_PRESETS,
  breakpoints,
  defaultTheme: DEFAULT_THEME_CONFIG,
  defaultLayoutPreset: DEFAULT_LAYOUT_PRESET,
  presetDefaults: LAYOUT_PRESET_DEFAULTS,
} as const;

export default LAYOUT_CONFIG;
